"use client"

import { Component, useState, type ReactNode } from "react"
import dynamic from "next/dynamic"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Info, Loader2, Map as MapIcon, Ruler } from "lucide-react"
import {
  formatArea,
  validateLawnSize,
  type AreaUnit,
  type LawnSizeInput,
} from "@/lib/lawn-size-calculator"
import { performanceAnalytics } from "@/lib/analytics"
import { isChunkLoadError, reloadWithCacheBuster } from "@/lib/chunk-error"

const loadLawnSizeMap = () =>
  import('./lawn-size-map').then(mod => ({ default: mod.LawnSizeMap }))

// Dynamic import for Google Maps component to reduce initial bundle size (~200KB).
// The dominant chunk failure here is a transient fetch abort on mobile Safari
// (backgrounded tab / flaky cellular), not a missing file, so one in-place
// retry absorbs most of them without a page reload.
const LawnSizeMap = dynamic(
  () =>
    loadLawnSizeMap().catch(() =>
      new Promise<void>(resolve => setTimeout(resolve, 500)).then(loadLawnSizeMap)
    ),
  {
    ssr: false, // Google Maps requires browser APIs
    loading: () => (
      <div style={{ height: 'clamp(460px, 82svh, 760px)' }} className="w-full rounded-lg border-2 border-gray-200 bg-gray-50 flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
        <div className="text-center">
          <p className="font-medium text-gray-700">Loading Map...</p>
          <p className="text-sm text-gray-500">Preparing satellite view</p>
        </div>
      </div>
    )
  }
)

interface MapLoadBoundaryProps {
  onRequestManualEntry: () => void
  children: ReactNode
}

/**
 * Contains a failed map chunk to the map panel. Without this the error
 * escapes to the route-level app/tools/error.tsx, which replaces the whole
 * page and takes the working manual-entry tab down with it.
 */
class MapLoadBoundary extends Component<MapLoadBoundaryProps, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: Error) {
    performanceAnalytics.captureError({
      error,
      source: "calculator",
      severity: "error",
      context: {
        surface: "lawn-size-map-boundary",
        chunkError: isChunkLoadError(error),
        recovery: "contained",
      },
    })
  }

  render() {
    if (!this.state.failed) return this.props.children

    return (
      <Card className="bg-red-50 border-red-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-red-900 mb-2">Map Couldn&apos;t Load</h3>
              <p className="text-sm text-red-800">
                The map failed to load on your connection. You can still measure your lawn by entering its dimensions.
              </p>
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <Button
                  type="button"
                  onClick={this.props.onRequestManualEntry}
                  className="h-10 bg-green-600 hover:bg-green-700 text-white"
                >
                  <Ruler className="w-4 h-4 mr-2" />
                  Enter dimensions manually
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => reloadWithCacheBuster()}
                  className="h-10"
                >
                  Reload page
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }
}

export interface LawnSizeMeasurement extends LawnSizeInput {
  hasAddress?: boolean
}

interface LawnSizeFormProps {
  onAreaCalculated?: (measurement: LawnSizeMeasurement) => void
  onMeasurementModeChange?: (method: LawnSizeInput['measurementMethod']) => void
  /** Forwarded from the map so the page can defer its result auto-scroll while
   *  the fullscreen layer holds a body scroll lock. */
  onMapFullscreenChange?: (fullscreen: boolean) => void
}

const AREA_UNITS: { value: AreaUnit; label: string }[] = [
  { value: 'sqft', label: 'sq ft' },
  { value: 'acres', label: 'acres' },
  { value: 'sqm', label: 'm²' },
]

export function LawnSizeForm({ onAreaCalculated, onMeasurementModeChange, onMapFullscreenChange }: LawnSizeFormProps = {}) {
  const [activeTab, setActiveTab] = useState<"map" | "manual">("map")
  const [areaUnit, setAreaUnit] = useState<AreaUnit>('sqft')

  // Manual entry state
  const [length, setLength] = useState("")
  const [width, setWidth] = useState("")

  const handleMapAreaCalculated = (area: number, _polygon: unknown, address: string, perimeter: number, vertexCount: number) => {
    // The page owns completed-measurement analytics AND the out-of-range
    // explanation (it renders a full result frame for invalid draws). This
    // component only emits the typed measurement, and deliberately reduces
    // the address to a boolean.
    onAreaCalculated?.({
      measurementMethod: 'map_draw',
      totalAreaSqFt: area,
      totalPerimeterFt: perimeter,
      vertexCount: vertexCount > 0 ? vertexCount : undefined,
      hasAddress: Boolean(address),
    })
  }

  const handleManualCalculate = () => {
    const l = parseFloat(length)
    const w = parseFloat(width)
    if (!l || !w || l <= 0 || w <= 0) return

    const area = Math.round(l * w)
    const perimeter = Math.round(2 * (l + w))
    if (!validateLawnSize(area).valid) return

    onAreaCalculated?.({
      measurementMethod: 'manual_rectangle',
      totalAreaSqFt: area,
      totalPerimeterFt: perimeter,
    })
  }

  const handleTabChange = (nextTab: "map" | "manual") => {
    if (nextTab === activeTab) return
    setActiveTab(nextTab)
    onMeasurementModeChange?.(nextTab === 'map' ? 'map_draw' : 'manual_rectangle')
  }

  const manualArea = (() => {
    const l = parseFloat(length)
    const w = parseFloat(width)
    if (l > 0 && w > 0) return Math.round(l * w)
    return null
  })()

  const manualPerimeter = (() => {
    const l = parseFloat(length)
    const w = parseFloat(width)
    if (l > 0 && w > 0) return Math.round(2 * (l + w))
    return null
  })()
  const manualValidation = manualArea !== null ? validateLawnSize(manualArea) : null
  const manualValidationError = manualValidation && !manualValidation.valid
    ? manualValidation.error
    : null

  return (
    <Card className="bg-white shadow-lg border-gray-200">
      <CardContent className="p-4 md:p-6">
        {/* Tab Toggle */}
        <div className="flex rounded-lg bg-gray-100 p-1 mb-4" role="tablist" aria-label="Lawn measurement method">
          <button
            type="button"
            onClick={() => handleTabChange("map")}
            role="tab"
            aria-selected={activeTab === "map"}
            aria-controls="lawn-size-map-panel"
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === "map"
                ? "bg-white text-gray-900 shadow-xs"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <MapIcon className="w-4 h-4" />
            Draw on Map
          </button>
          <button
            type="button"
            onClick={() => handleTabChange("manual")}
            role="tab"
            aria-selected={activeTab === "manual"}
            aria-controls="lawn-size-manual-panel"
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === "manual"
                ? "bg-white text-gray-900 shadow-xs"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <Ruler className="w-4 h-4" />
            Enter Dimensions
          </button>
        </div>

        {/* Map Tab */}
        {activeTab === "map" && (
          <div id="lawn-size-map-panel" role="tabpanel">
            <MapLoadBoundary onRequestManualEntry={() => handleTabChange("manual")}>
              <LawnSizeMap
                onAreaCalculated={handleMapAreaCalculated}
                onRequestManualEntry={() => handleTabChange("manual")}
                onFullscreenChange={onMapFullscreenChange}
              />
            </MapLoadBoundary>
          </div>
        )}

        {/* Manual Entry Tab */}
        {activeTab === "manual" && (
          <div id="lawn-size-manual-panel" role="tabpanel" className="space-y-5">
            <p className="text-sm text-gray-600">
              Enter the length and width of your lawn to calculate the total area. For irregular shapes, measure the longest and widest points for an estimate.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lawn-length" className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Length (ft)
                </Label>
                <Input
                  id="lawn-length"
                  type="number"
                  placeholder="e.g., 100"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="1"
                  aria-invalid={Boolean(manualValidationError)}
                  aria-describedby={manualValidationError ? 'lawn-size-validation-error' : undefined}
                  className="h-11"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleManualCalculate() }}
                />
              </div>
              <div>
                <Label htmlFor="lawn-width" className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Width (ft)
                </Label>
                <Input
                  id="lawn-width"
                  type="number"
                  placeholder="e.g., 50"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  min="1"
                  aria-invalid={Boolean(manualValidationError)}
                  aria-describedby={manualValidationError ? 'lawn-size-validation-error' : undefined}
                  className="h-11"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleManualCalculate() }}
                />
              </div>
            </div>

            {/* Area unit toggle */}
            <div
              className="flex rounded-lg bg-gray-100 p-1"
              role="group"
              aria-label="Area unit"
            >
              {AREA_UNITS.map(unit => (
                <button
                  key={unit.value}
                  type="button"
                  onClick={() => setAreaUnit(unit.value)}
                  aria-pressed={areaUnit === unit.value}
                  className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    areaUnit === unit.value
                      ? "bg-white text-gray-900 shadow-xs"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {unit.label}
                </button>
              ))}
            </div>

            {/* Live area preview */}
            {manualArea !== null && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <span className="text-sm text-green-700">Estimated Area</span>
                <div className="text-2xl font-bold text-green-800 mt-1">
                  {formatArea(manualArea, areaUnit)}
                </div>
                {areaUnit !== 'acres' && (
                  <span className="text-xs text-green-600">
                    ({(manualArea / 43560).toFixed(3)} acres)
                  </span>
                )}
                {manualPerimeter !== null && (
                  <div className="mt-2 pt-2 border-t border-green-200">
                    <span className="text-sm text-green-700">Perimeter / edging length</span>
                    <div className="text-lg font-bold text-green-800">
                      {manualPerimeter.toLocaleString()} ft
                    </div>
                  </div>
                )}
              </div>
            )}

            {manualValidationError && (
              <p id="lawn-size-validation-error" role="alert" className="text-sm font-medium text-red-700">
                {manualValidationError}
              </p>
            )}

            <Button
              onClick={handleManualCalculate}
              disabled={!manualArea || Boolean(manualValidationError)}
              className="w-full bg-green-600 hover:bg-green-700 text-white h-11"
            >
              Calculate
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
